import {
  BasePlanDetails,
  FormattedPlanDetails,
  GetPaymentLinkRequest,
  PromoCodePlansPricesType,
  SubscriptionDuration,
  SubscriptionPlanType,
} from "@shared/types";
import { round } from "@shared/utils/numbers";
import {
  addFeeToPrice,
  convertPriceToMonthlyPrice,
  getInitialPrice,
  getInitialTotalPrice,
  getPlanPrice,
  getPrices,
  preprocessPlansData,
} from "@shared/features/settings/subscription/utils";
import { useTheme } from "styled-components";
import { sharedLib } from "@shared/services/shared.service";
import { CancelSubscriptionContext } from "@shared/features/settings/subscription/CancelSubscription.context";
import { useContext, useMemo, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";

interface usePromoPricesProps {
  plan: FormattedPlanDetails | undefined;
  duration: SubscriptionDuration;
  isUpgrade?: boolean;
  promoCodePrices?: PromoCodePlansPricesType;
  isCrypto?: boolean;
}

export const usePromoPrices = ({
  plan,
  duration,
  isUpgrade,
  promoCodePrices,
  isCrypto,
}: usePromoPricesProps) => {
  const theme = useTheme();
  const {
    planPrice,
    extraFee: planExtraFee,
    totalPrice,
  } = getPrices(plan, duration, !!isUpgrade, undefined, isCrypto);
  const { extraFee: promoCodeExtraFee, totalPrice: promoCodeTotalPrice } =
    getPrices(
      plan,
      duration,
      !!isUpgrade,
      promoCodePrices?.[duration],
      isCrypto
    );
  const extraFee = promoCodeExtraFee || planExtraFee;
  const newPlan = isUpgrade ? plan?.upgradePlan : plan?.[duration];

  const getPlanPrices = (
    plan?: BasePlanDetails,
    promoCodePlanPrice?: number
  ) => {
    const price = promoCodePlanPrice || getPlanPrice(plan);
    const monthlyPrice = plan
      ? convertPriceToMonthlyPrice(price, plan.subscription_days)
      : 0;
    return [price, monthlyPrice];
  };

  const [originalPlanPrice, originalPlanPricePerMonth] = getPlanPrices(newPlan);

  const initialUpgradePrice = round(
    getInitialPrice(plan, duration) - originalPlanPrice + (planPrice || 0),
    2
  );

  const initialUpgradeTotalPrice = addFeeToPrice(initialUpgradePrice, isCrypto);

  const initialTotalPrice = isUpgrade
    ? initialUpgradeTotalPrice
    : getInitialTotalPrice(plan, duration, isCrypto);

  const isPromoCodeApplied = promoCodePrices?.[duration]?.code === 200;
  const promoCodeMessage = promoCodePrices?.[duration]?.message;

  const discount = isPromoCodeApplied
    ? round(
        ((initialTotalPrice - promoCodeTotalPrice) / initialTotalPrice) * 100,
        0
      )
    : 0;

  const getPromoCodeDiscountMessage = () => {
    if (isPromoCodeApplied) {
      return `${discount}% discount applied`;
    }
  };

  const getResultMessageColor = () => {
    if (isPromoCodeApplied) {
      if (promoCodeMessage?.length) return theme.colors.gold;
      else return theme.colors.green;
    }
    return theme.colors.red;
  };

  return {
    discount,
    extraFee,
    initialTotalPrice,
    originalPlanPricePerMonth,
    totalPrice,
    promoCodeTotalPrice,
    newPlan,
    originalPlanPrice,
    initialUpgradePrice,
    getPlanPrices,
    planPrice,
    getPromoCodeDiscountMessage,
    getResultMessageColor,
    promoCodeMessage,
    isPromoCodeApplied,
  };
};

export const useGetSubscriptionPlans = (
  subGroupId?: number,
  promoCode?: string | null,
  skip?: boolean
) => {
  const cancelSubscriptionData = useContext(CancelSubscriptionContext);

  const args = useMemo(() => {
    if (skip) return skipToken;
    return subGroupId || promoCode
      ? { sub_group_id: subGroupId, promo_code: promoCode }
      : undefined;
  }, [promoCode, subGroupId, skip]);

  return sharedLib.sharedApi.settings.useGetAllPlanDetailsQuery(args, {
    selectFromResult: ({ data: plans, isFetching, isLoading }) => {
      let filteredPlans = plans;
      if (cancelSubscriptionData) {
        filteredPlans = filteredPlans?.filter(
          (plan) => plan.subscription_plan_type !== SubscriptionPlanType.FREE
        );
      }
      return {
        plans: preprocessPlansData(filteredPlans || []),
        isFetching: isFetching,
        isLoading,
      };
    },
  });
};

export const usePromoCodePrices = () => {
  const [getNewPaymentPrice, getNewPaymentPriceStatus] =
    sharedLib.sharedApi.settings.useLazyGetNewPaymentPriceQuery();

  const [promoCodePrices, setPromoCodePrices] = useState<
    PromoCodePlansPricesType | undefined
  >();

  const handleGetNewPaymentPrice = (
    args: Omit<GetPaymentLinkRequest, "email">,
    duration: number
  ) => {
    getNewPaymentPrice(args)
      .unwrap()
      .then((response) => {
        setPromoCodePrices((prevPrices) => ({
          ...prevPrices,
          [duration]: response,
        }));
      })
      .catch((response) => {
        setPromoCodePrices((prevPrices) => ({
          ...prevPrices,
          [duration]: { message: response.data.message },
        }));
      });
  };

  return {
    handleGetNewPaymentPrice,
    getNewPaymentPriceStatus,
    promoCodePrices,
    setPromoCodePrices,
  };
};
